import { TextInput, Group, Button, Text, Box, Anchor, Image, Divider, Center, Flex } from '@mantine/core'
import { UseFormReturnType, isEmail, useForm } from '@mantine/form'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { IconPassword, IconMail } from '@tabler/icons-react'
import { Logo } from 'components/logo'
import { useMediaQuery } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'

interface FormValues {
  email: string
  password: string
}

export const Login = () => {
  const { login } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const isSmallDevice = useMediaQuery('(max-width: 864px)')

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: isEmail('Invalid email'),
    },
  })

  async function onSubmit(values: FormValues) {
    setIsLoading(true)
    await login(values.email, values.password)
    setIsLoading(false)
  }

  if (isSmallDevice) {
    return (
      <Box w="100%" display="flex" sx={{ flexDirection: 'column' }}>
        <Box pt={10} pl={30}>
          <Logo width={100} />
        </Box>
        <Box
          pl={30}
          pr={30}
          mt={10}
          mb={50}
          h={140}
          sx={(theme) => ({
            background: theme.colors['custom-primary-color'][0],
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            color: theme.white,
          })}
        >
          <Text align="center" weight={500} size="18px">
            {t('authentication.welcome')}
          </Text>
          <Text align="center" weight={700} size="30px">
            {t('authentication.please-login')}
          </Text>
        </Box>
        <LoginForm form={form} isLoading={isLoading} onSubmit={onSubmit} t={t} navigate={navigate} />
      </Box>
    )
  }

  return (
    <>
      <Box display="flex" sx={{ flex: 1, flexFlow: 'column', padding: '20px 30px', zIndex: 100 }}>
        <Logo />
        <Box mt={90} mb={30} sx={{ width: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
          <Text align="center" weight={500} size="25px">
            {t('authentication.welcome')}
          </Text>
          <Text align="center" weight={700} size="40px">
            {t('authentication.please-login')}
          </Text>
        </Box>

        <LoginForm form={form} isLoading={isLoading} onSubmit={onSubmit} t={t} navigate={navigate} />
      </Box>
      <Box
        w={400}
        bg="custom-primary-color.0"
        display="flex"
        sx={{
          overflow: 'hidden',
          flexFlow: 'column',
          alignItems: 'center',
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
          color: 'white',
          paddingTop: '237px',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: 5, left: '-100px', right: 0, bottom: 0, height: '100%', minWidth: '1264px' }}>
          <Image src="https://cdn.expovisio.com/full-banner.png" alt="additional-img" />
        </Box>
      </Box>
    </>
  )
}

interface LoginFormProps {
  form: UseFormReturnType<
    {
      email: string
      password: string
    },
    (values: { email: string; password: string }) => {
      email: string
      password: string
    }
  >
  onSubmit: (values: FormValues) => Promise<void>
  t: (key: string) => string
  navigate: (path: string) => void
  isLoading: boolean
}

function LoginForm({ form, isLoading, navigate, onSubmit, t }: LoginFormProps) {
  const { signInWithEmail } = useAuth()

  const [passwordlessLoginLoading, setPasswordlessLoginLoading] = useState(false)

  const passwordlessForm = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: isEmail('Invalid email'),
    },
  })

  const onSubmitPasswordless = async (values: { email: string }) => {
    setPasswordlessLoginLoading(true)
    showNotification({
      title: t('authentication.passwordless-login'),
      message: t('authentication.check-your-email'),
      color: 'blue',
    })

    try {
      await signInWithEmail(values.email)
      passwordlessForm.reset() // Clear the form after successful submission
    } catch (error) {
      console.error('Error during passwordless login:', error)
    }

    setPasswordlessLoginLoading(false)
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Flex maw={650} w="100%" justify="center" direction="column">
        <form onSubmit={passwordlessForm.onSubmit(onSubmitPasswordless)}>
          <Box w="70%" miw={350} mx="auto">
            <TextInput
              size="md"
              placeholder={t('authentication.email') || ''}
              icon={<IconMail width={15} height={15} stroke="#cecece" />}
              {...passwordlessForm.getInputProps('email')}
            />
            <Button mt={20} w="100%" color="custom-primary-color.0" radius="md" size="md" type="submit" loading={passwordlessLoginLoading} loaderPosition="right">
              {t('common.login-with-email')}
            </Button>
          </Box>
        </form>

        <Divider label="OR" my={20} labelPosition="center" labelProps={{ size: 'lg' }} />
        <Box w="70%" miw={350} mx="auto">
          <form onSubmit={form.onSubmit(onSubmit)}>
            <Box mb={10}>
              <TextInput size="md" icon={<IconMail width={15} height={15} stroke="#cecece" />} placeholder={t('authentication.email') || ''} {...form.getInputProps('email')} />
            </Box>

            <Box mb={20}>
              <TextInput
                mb={5}
                size="md"
                icon={<IconPassword width={15} height={15} stroke="#cecece" />}
                type="password"
                placeholder={t('authentication.password') || ''}
                {...form.getInputProps('password')}
              />
              <Box mt={10} display="flex" sx={{ justifyContent: 'flex-end' }}>
                <Anchor size="sm" underline onClick={() => navigate('/session/forgot-password')}>
                  {t('authentication.forgot-password')}
                </Anchor>
              </Box>
            </Box>

            <Group position="center">
              <Button size="md" w="100%" color="custom-primary-color.0" radius="md" type="submit" loading={isLoading} loaderPosition="right">
                {t('common.login')}
              </Button>
            </Group>
          </form>
        </Box>
      </Flex>
    </Box>
  )
}
